import { ChangeDetectionStrategy, ChangeDetectorRef, Component, OnInit, ViewChild, inject } from '@angular/core';
import { AlertService, PaginatorFetcher, PaginatorFilters, TableComponent, TableConfig } from '@michelin/theme';
import { FormArray, FormBuilder, FormControl, FormGroup } from '@angular/forms';
import { Pagination, RerunReworkReasonsFiltersResponse } from '../../models/response.model';
import { map, Observable } from 'rxjs';
import { TranslationService } from '../../../../shared/translations/services/translation.service';
import { ProductDataService } from '../../services/product-data.service';
import { Store } from '@ngxs/store';
import { TranslationState } from '../../../../shared/state/TranslationState';
import { debounceTime, distinctUntilChanged } from 'rxjs/operators';
import { RerunReworkReason } from '../../models/rerun-rework-reason.model';
import { ExportResponse } from '../../../../shared/services/export-to-excel.service';
import { DealerState } from '../../../../shared/state/DealerState';
import { MatDialog } from '@angular/material/dialog';
import { ExportModalComponent, ExportModalData } from '../../../../shared/components/export-modal/export-modal.component';
import { ExportColumnDef } from '../../../../shared/models/export-column-def.model';

@Component({
  selector: 'app-rerun-rework-reason',
  templateUrl: './rerun-rework-reason.component.html',
  styleUrl: './rerun-rework-reason.component.scss',
  standalone: false,
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class RerunReworkReasonComponent implements OnInit {
  @ViewChild(TableComponent, { static: true }) tableRef: TableComponent<RerunReworkReason>;
  rerunReasonList: any[] = [];
  descriptionList: any[] = [];
  typeList: any[] = [];
  cureProcessList: any[] = [];
  activeList: any[] = [];

  public translationService = inject(TranslationService);
  public productDataService = inject(ProductDataService);
  public alertService = inject(AlertService);
  public fb = inject(FormBuilder);
  public store = inject(Store);
  public cdr = inject(ChangeDetectorRef);
  private dialog = inject(MatDialog);

  filterForm: FormGroup;
  filterFormBackend: FormGroup;
  rerunReworkReasons: RerunReworkReason[] = [];
  plants: string[] = [];
  displayedRowsForm: FormGroup = this.fb.group({ rows: this.fb.array([]) });
  pagination: Pagination;
  pageSizeOptions = [10, 25, 50, 100];
  currentPage = 1;
  pageSize = 10;

  openExportModal(): void {
    const columns: ExportColumnDef[] = Object.entries(this.columnNames).map(([key, label]) => ({ key, label, selected: true }));
    const data: ExportModalData = {
      columns,
      dataFetcher: this.exportDataFetcher,
      fileNamePrefix: this.dealerName + '-RerunReworkReason-export',
      maxExportRows: 10000
    };
    this.dialog.open(ExportModalComponent, { data, width: '480px' });
  }

  tableConfig: TableConfig<RerunReworkReason> = {
    paginator: {
      pageSize: this.pageSize,
      pageSizeOptions: this.pageSizeOptions,
      forceBottomAction: true,
      position: 'bottom'
    },
    columns: {}
  };
  dealerName = this.store.selectSnapshot(DealerState.currentDealer).name;
  columnNames: Record<string, string> = {};

  exportDataFetcher: () => Observable<ExportResponse> = () =>
    this.productDataService.getRerunReworkReasons(this.filterFormBackend.value, 1, 10000).pipe(
      map((response: any) => ({
        data: (response.data.rerunReworkReasons as RerunReworkReason[]).map((item) => ({
          rerun: item.rerun,
          description: item.description,
          reasonType: item.reasonType,
          cureProcess: item.cureProcess,
          active: item.active === 'Y' ? this.translationService.translate('yesText') : this.translationService.translate('noText')
        }))
      }))
    );

  fetcher: PaginatorFetcher<RerunReworkReason> = (filters: PaginatorFilters) => {
    this.currentPage = filters.page;
    this.pageSize = filters.limit;
    this.generateFilterForm();
    return this.productDataService.getRerunReworkReasons({ ...this.filterFormBackend.value, ...filters }, filters.page, filters.limit).pipe(
      map((response: any) => {
        this.rerunReworkReasons = response.data.rerunReworkReasons || [];
        this.pagination = response.data.pagination;
        this.updateDisplayedRowsForm(this.rerunReworkReasons);
        return { items: this.rerunReworkReasons, count: this.pagination.totalRecords };
      })
    );
  };

  constructor() {
    this.filterForm = this.fb.group({
      rerunReason: [[]],
      description: [[]],
      type: [[]],
      cureProcess: [[]],
      active: [[]]
    });
    this.filterFormBackend = this.fb.group({
      rerunReason: [[]],
      description: [[]],
      type: [[]],
      cureProcess: [[]],
      active: [[]]
    });
  }

  ngOnInit(): void {
    this.productDataService.getRerunReworkReasonsFilters().subscribe((response: RerunReworkReasonsFiltersResponse) => {
      this.rerunReasonList = response.data.reruns || [];
      this.descriptionList = response.data.descriptions || [];
      this.typeList = response.data.reasonTypes || [];
      this.cureProcessList = response.data.cureProcesses || [];
      // Replace 'Y' with 'Yes', 'N' with 'No', keep others as is
      this.activeList = (response.data.activeList || []).map((v) => (v === 'Y' ? 'Yes' : v === 'N' ? 'No' : v));
      this.filterForm.patchValue({
        rerunReason: [],
        description: [],
        type: [],
        cureProcess: [],
        active: []
      });
    });
    this.filterForm.valueChanges.pipe(distinctUntilChanged(), debounceTime(800)).subscribe(() => {
      this.generateFilterForm();
      this.tableRef?.patchExtraFilters(this.filterFormBackend.value, false);
    });
    this.store.select(TranslationState.currentLanguage).subscribe(() => {
      this.columnNames = {
        rerun: this.translationService.translate('rerunReworkReasonText'),
        description: this.translationService.translate('descriptionText'),
        reasonType: this.translationService.translate('typeColumnText'),
        cureProcess: this.translationService.translate('cureProcessText'),
        active: this.translationService.translate('activeText')
      };

      this.tableConfig.columns = {
        rerunReason: { cell: (row) => row.rerun, header: this.translationService.translate('rerunReworkReasonText') },
        description: { cell: (row) => row.description, header: this.translationService.translate('descriptionText') },
        type: { cell: (row) => row.reasonType, header: this.translationService.translate('typeColumnText') },
        cureProcess: { cell: (row) => row.cureProcess, header: this.translationService.translate('cureProcessText') },
        active: {
          cell: (row) => (row.active === 'Y' ? 'Yes' : row.active === 'N' ? 'No' : row.active),
          header: this.translationService.translate('activeText')
        }
      };
      this.cdr.markForCheck();
    });
  }

  createRowForm(row: RerunReworkReason): FormGroup {
    return this.fb.group({
      rerunReason: [row.rerun],
      description: [row.description],
      type: [row.reasonType],
      cureProcess: [row.cureProcess],
      active: [row.active]
    });
  }

  updateDisplayedRowsForm(rows: RerunReworkReason[]) {
    const rowForms = rows.map((row) => this.createRowForm(row));
    const formArray = this.fb.array(rowForms);
    this.displayedRowsForm.setControl('rows', formArray);
  }

  getControl(item: RerunReworkReason, controlName: keyof RerunReworkReason): FormControl {
    const index = this.rerunReworkReasons.findIndex((row) => row.rerun === item.rerun);
    if (index === -1) {
      return new FormControl('');
    }
    return (this.displayedRowsForm.get('rows') as FormArray).at(index).get(controlName) as FormControl;
  }

  clearFilters(): void {
    this.filterForm.reset({
      rerunReason: [],
      description: [],
      type: [],
      cureProcess: [],
      active: []
    });
  }

  generateFilterForm(): void {
    const filterRerunReason = this.filterForm.get('rerunReason').value;
    const filterDescription = this.filterForm.get('description').value;
    const filterType = this.filterForm.get('type').value;
    const filterCureProcess = this.filterForm.get('cureProcess').value;
    const filterActive = this.filterForm.get('active').value;
    // Replace 'Yes' with 'Y' and 'No' with 'N' for backend filters
    const backendActive = (filterActive || []).map((v) => (v === 'Yes' ? 'Y' : v === 'No' ? 'N' : v));
    this.filterFormBackend.patchValue({
      rerunReason: filterRerunReason.length > 0 && filterRerunReason.length == this.rerunReasonList.length ? [] : filterRerunReason,
      description: filterDescription.length > 0 && filterDescription.length == this.descriptionList.length ? [] : filterDescription,
      type: filterType.length > 0 && filterType.length == this.typeList.length ? [] : filterType,
      cureProcess: filterCureProcess.length > 0 && filterCureProcess.length == this.cureProcessList.length ? [] : filterCureProcess,
      active: backendActive.length > 0 && backendActive.length == this.activeList.length ? [] : backendActive
    });
  }
}
